import { useState, useEffect, useRef } from "react";
import { Skeleton, Tooltip, Input, Switch, Select, Modal } from "antd";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import {
  FileTextOutlined, ReloadOutlined, SaveOutlined, UploadOutlined,
  HolderOutlined, ArrowUpOutlined, ArrowDownOutlined,
  PlusOutlined, DeleteOutlined,
} from "@ant-design/icons";
import { isSuperAdmin } from "../utils/role";
import {
  useProposalTemplate, usePatchProposalTemplate,
  useReorderSections, useUpdateSection, useAddSection, useDeleteSection,
} from "../hooks/useRateCard";
import { uploadProposalImage } from "../services/rateCardService";
import {
  TextField, NumField, HtmlField, ListEditor, PlaceholderHint, label,
} from "./contentEditors";

/**
 * Proposal & Quotation Content.
 *
 * Two documents, each an ordered list of sections. A section's array position is
 * its print order, and its `type` decides what its `content` looks like — so
 * every editor here is chosen by type, not by a hardcoded list of section names.
 *
 * Saving is per section: title, enabled and content go through one endpoint, the
 * order through another. `content` is replaced whole, never merged.
 */

const card = {
  background: "white", borderRadius: 16, border: "1px solid #f0f0f0",
  boxShadow: "0 1px 4px rgba(0,0,0,0.04)", overflow: "hidden", marginBottom: 20,
};
const cardHead = {
  padding: "14px 20px", borderBottom: "1px solid #f0f0f0", background: "#fafbff",
  display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
};
const cardTitle = { fontSize: 15, fontWeight: 700, color: "#111827" };
const body = { padding: 20, display: "grid", gap: 20 };
const cols = (min = 220) => ({ display: "grid", gridTemplateColumns: `repeat(auto-fit,minmax(${min}px,1fr))`, gap: 20 });

const DOCS = [
  { key: "proposal", label: "Proposal" },
  { key: "quotation", label: "Quotation" },
];

/** What each section type is, in the words an editor would use. */
const TYPE_LABEL = {
  executive_summary: "Executive summary",
  why_entermark: "Stats & cards",
  offerings: "Offerings",
  list: "Bullet list",
  steps: "Numbered steps",
  founder: "Founder",
  acceptance: "Signatures",
  terms: "Terms",
  richtext: "Rich text",
};

/* Only these can be added by hand: the rest are one-of-a-kind blocks the backend
   ships with the document. */
const ADDABLE_TYPES = ["richtext", "list", "steps", "terms"];

const blankContent = (type) => {
  if (type === "list" || type === "terms") return { items: [] };
  if (type === "steps") return { heading: "", steps: [] };
  return { html: "" };
};

/* ── Founder photo ────────────────────────────────────────────────────────── */
const FounderPhoto = ({ url, name, editable, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [showUrlField, setShowUrlField] = useState(false);
  const inputRef = useRef(null);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    // Let the same file be chosen again after a failed attempt.
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      onChange(await uploadProposalImage(file));
      toast.success("Photo uploaded — remember to save this section.");
    } catch (err) {
      toast.error(err?.message || "The image could not be uploaded");
    } finally {
      setUploading(false);
    }
  };

  const thumb = { width: 76, height: 76, borderRadius: 14, objectFit: "cover", border: "2px solid #eef2ff", flexShrink: 0 };
  const btn = {
    padding: "7px 16px", borderRadius: 9, fontSize: 12.5, fontWeight: 700, cursor: "pointer",
    border: "1px solid #ddd6fe", background: "#f5f3ff", color: "#5b48e8",
  };

  return (
    <div>
      <label style={label}>Founder photo</label>
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
        {url ? (
          <img src={url} alt={name || "Founder"} style={thumb} />
        ) : (
          /* The same fallback the document renders, so the preview never lies. */
          <div style={{ ...thumb, background: "linear-gradient(135deg,#5b48e8,#7c3aed)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, fontWeight: 800 }}>
            {(name || "?").trim().split(/\s+/).map((w) => w[0]).join("").toUpperCase().slice(0, 2) || "?"}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 220 }}>
          {editable ? (
            <>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <input ref={inputRef} type="file" accept="image/*" onChange={pick} style={{ display: "none" }} />
                <button style={btn} onClick={() => inputRef.current?.click()} disabled={uploading}>
                  <UploadOutlined /> {uploading ? "Uploading…" : url ? "Replace photo" : "Upload photo"}
                </button>
                {url && (
                  <button
                    style={{ ...btn, border: "1px solid #fecaca", background: "#fef2f2", color: "#dc2626" }}
                    onClick={() => onChange(null)}
                    disabled={uploading}
                  >
                    Remove
                  </button>
                )}
              </div>
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 8, lineHeight: 1.5 }}>
                No photo means the initials avatar above.{" "}
                <button
                  onClick={() => setShowUrlField((v) => !v)}
                  style={{ border: "none", background: "none", padding: 0, color: "#5b48e8", fontWeight: 700, cursor: "pointer", fontSize: 11 }}
                >
                  {showUrlField ? "Hide URL field" : "Paste a URL instead"}
                </button>
              </div>
              {showUrlField && (
                <div style={{ marginTop: 10 }}>
                  <Input value={url ?? ""} onChange={(e) => onChange(e.target.value || null)} placeholder="https://… (absolute URL)" />
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 5 }}>
                    Must be absolute — the document is printed and emailed, where a relative path
                    resolves to nothing.
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{ fontSize: 12.5, color: url ? "#6b7280" : "#d1d5db", wordBreak: "break-all" }}>
              {url || "No photo — the initials avatar is used."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Per-audience copy ────────────────────────────────────────────────────── */
/**
 * Some sections read differently to a corporate client than to a law firm, so
 * the backend stores those keyed by segment and resolves them only in
 * /rendered. The editor gets the raw config, so it has to pick an audience —
 * and it must write back the other audiences untouched.
 */
const SEG_LABEL = { corporate: "Corporate", attorney: "Attorney" };
const segLabel = (key) => SEG_LABEL[key] || key.replace(/[-_]/g, " ");

const SegmentTabs = ({ segments, value, onChange }) => (
  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
    <span style={{ ...label, marginBottom: 0 }}>Audience</span>
    {segments.map((s) => {
      const on = s === value;
      return (
        <button
          key={s}
          onClick={() => onChange(s)}
          style={{
            padding: "4px 13px", borderRadius: 20, fontSize: 12, fontWeight: 700,
            cursor: "pointer", textTransform: "capitalize",
            border: `1px solid ${on ? "#5b48e8" : "#e5e7eb"}`,
            background: on ? "#f5f3ff" : "#fff",
            color: on ? "#5b48e8" : "#6b7280",
          }}
        >
          {segLabel(s)}
        </button>
      );
    })}
    <span style={{ fontSize: 11, color: "#9ca3af" }}>
      Each audience has its own copy — editing one leaves the other alone.
    </span>
  </div>
);

/** Keeps the picked audience valid when the section it belongs to changes. */
const useSegment = (segments) => {
  const [seg, setSeg] = useState(segments[0] || null);
  const joined = segments.join("|");
  useEffect(() => {
    if (segments.length && !segments.includes(seg)) setSeg(segments[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joined]);
  return [segments.includes(seg) ? seg : segments[0] || null, setSeg];
};

const OfferingsEditor = ({ content, editable, onChange }) => {
  const c = content || {};
  /* Segment-keyed in the config; a flat items[] is what /rendered returns and
     what an older config may still hold. */
  const segments = Object.keys(c).filter((k) => k !== "items" && Array.isArray(c[k]));
  const [seg, setSeg] = useSegment(segments);
  const items = (seg ? c[seg] : c.items) || [];

  return (
    <>
      {segments.length > 0 && <SegmentTabs segments={segments} value={seg} onChange={setSeg} />}
      <ListEditor
        items={items}
        onChange={(v) => onChange(seg ? { ...c, [seg]: v } : { ...c, items: v })}
        editable={editable}
        addLabel="Add offering" blank={{ icon: "", title: "", description: "" }}
        fields={[
          { key: "icon", title: "Icon (emoji)", placeholder: "🛡" },
          { key: "title", title: "Title" },
          { key: "description", title: "Description", type: "html", rows: 2, placeholders: false },
        ]}
      />
    </>
  );
};

const ExecSummaryEditor = ({ content, editable, onChange }) => {
  const c = content || {};
  const set = (key, value) => onChange({ ...c, [key]: value });
  /* Only the body is written per audience; the heading and subheading are shared. */
  const perSegment = c.body && typeof c.body === "object" && !Array.isArray(c.body);
  const segments = perSegment ? Object.keys(c.body) : [];
  const [seg, setSeg] = useSegment(segments);

  return (
    <>
      <TextField title="Subheading" value={c.subheading} editable={editable}
        onChange={(v) => set("subheading", v)} />
      {editable && <PlaceholderHint />}
      {perSegment && <SegmentTabs segments={segments} value={seg} onChange={setSeg} />}
      <HtmlField
        title="Body" rows={6} editable={editable}
        value={perSegment ? c.body[seg] : c.body}
        onChange={(v) => set("body", perSegment ? { ...c.body, [seg]: v } : v)}
      />
    </>
  );
};

/* ── Signature block ──────────────────────────────────────────────────────── */
/**
 * The wording of the acceptance page. The two organisation names, the CIN, the
 * signatory, the signature scan and the seal all come from Company Profile —
 * both documents sign with the same pair, so only the labels live here.
 */
const SignatureNote = ({ company }) => {
  const c = company || {};
  const line = [c.legal_name, c.signatory_name, c.signatory_role].filter(Boolean).join(" · ");

  return (
    <div style={{ background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 10, padding: "14px 16px" }}>
      <div style={{ display: "flex", gap: 18, alignItems: "center", flexWrap: "wrap" }}>
        {/* Read-only on purpose: it is uploaded on Company Profile, because the
            quotation signs with the same one. Shown here so nobody has to open
            another screen to find out what will print. */}
        {c.signature_url ? (
          <img
            src={c.signature_url} alt="Authorised signature"
            style={{ height: 52, maxWidth: 200, objectFit: "contain", background: "#fff", border: "1px solid #99f6e4", borderRadius: 8, padding: 6 }}
          />
        ) : (
          <span style={{ fontSize: 12.5, color: "#0f766e", fontStyle: "italic" }}>
            No signature uploaded — the document draws one.
          </span>
        )}
        {line && <span style={{ fontSize: 12.5, color: "#0f766e", fontWeight: 700 }}>{line}</span>}
      </div>
      <div style={{ fontSize: 12.5, color: "#0f766e", lineHeight: 1.6, marginTop: 12 }}>
        The organisation name, CIN, signatory, signature image and seal come from{" "}
        <b>Company Profile</b> — the quotation signs with the same pair. Not editable here.
      </div>
    </div>
  );
};

/* ── Section content, one editor per type ─────────────────────────────────── */
const SectionContent = ({ type, content, editable, onChange, company }) => {
  const c = content || {};
  const set = (key, value) => onChange({ ...c, [key]: value });

  switch (type) {
    case "executive_summary":
      return <ExecSummaryEditor content={c} editable={editable} onChange={onChange} />;

    case "why_entermark":
      return (
        <>
          <TextField title="Subheading" value={c.subheading} editable={editable}
            onChange={(v) => set("subheading", v)} />
          <div>
            <label style={label}>Stat tiles</label>
            <ListEditor
              items={c.stats || []} onChange={(v) => set("stats", v)} editable={editable}
              addLabel="Add stat" blank={{ value: "", label: "" }}
              fields={[
                { key: "value", title: "Value", placeholder: "75,00,000+" },
                { key: "label", title: "Label", placeholder: "Marks monitored" },
              ]}
            />
          </div>
          <div>
            <label style={label}>Cards</label>
            <ListEditor
              items={c.cards || []} onChange={(v) => set("cards", v)} editable={editable}
              addLabel="Add card" blank={{ title: "", description: "" }}
              fields={[
                { key: "title", title: "Title" },
                { key: "description", title: "Description", type: "html", rows: 2, placeholders: false },
              ]}
            />
          </div>
        </>
      );

    case "offerings":
      return <OfferingsEditor content={c} editable={editable} onChange={onChange} />;

    case "list":
      return (
        <ListEditor
          items={c.items || []} onChange={(v) => set("items", v)} editable={editable}
          addLabel="Add item" blank=""
          // A plain string list: the row itself is the value.
          fields={[{ key: null, title: "Item", placeholder: "AI Trademark Search" }]}
        />
      );

    case "steps":
      return (
        <ListEditor
          items={c.steps || []} onChange={(v) => set("steps", v)} editable={editable}
          addLabel="Add step" numbered blank=""
          fields={[{ key: null, title: "Step", type: "html", rows: 2 }]}
        />
      );

    case "terms":
      return (
        <ListEditor
          items={c.items || []} onChange={(v) => set("items", v)} editable={editable}
          addLabel="Add term" numbered blank=""
          fields={[{ key: null, title: "Term", type: "html", rows: 3 }]}
        />
      );

    case "founder":
      return (
        <>
          <div style={cols()}>
            <TextField title="Subheading" value={c.subheading} editable={editable} onChange={(v) => set("subheading", v)} />
            <TextField title="Name" value={c.name} editable={editable} onChange={(v) => set("name", v)} />
            <TextField title="Title" value={c.title} editable={editable} onChange={(v) => set("title", v)} />
          </div>
          <FounderPhoto url={c.photo_url} name={c.name} editable={editable} onChange={(v) => set("photo_url", v)} />
          <HtmlField title="Bio" rows={4} editable={editable} value={c.bio} onChange={(v) => set("bio", v)} showPlaceholders={false} />
          <HtmlField title="Quote" rows={3} editable={editable} value={c.quote} onChange={(v) => set("quote", v)} showPlaceholders={false} />
        </>
      );

    case "acceptance":
      return (
        <>
          <HtmlField title="Subheading" rows={2} editable={editable} value={c.subheading}
            onChange={(v) => set("subheading", v)} />
          <div style={cols()}>
            <TextField title="Client block heading" value={c.client_label} editable={editable}
              onChange={(v) => set("client_label", v)} />
            <TextField title="Company block heading" value={c.company_label} editable={editable}
              onChange={(v) => set("company_label", v)} />
          </div>
          <HtmlField title="Client confirmation line" rows={2} editable={editable} value={c.client_note}
            onChange={(v) => set("client_note", v)} />
          <div style={cols(160)}>
            <TextField title="Signature line label" value={c.sign_label} editable={editable}
              onChange={(v) => set("sign_label", v)} />
            <TextField title="Name label" value={c.name_label} editable={editable}
              onChange={(v) => set("name_label", v)} />
            <TextField title="Designation label" value={c.designation_label} editable={editable}
              onChange={(v) => set("designation_label", v)} />
            <TextField title="Date label" value={c.date_label} editable={editable}
              onChange={(v) => set("date_label", v)} />
          </div>
          <SignatureNote company={company} />
        </>
      );

    case "richtext":
      return <HtmlField title="Content" rows={8} editable={editable} value={c.html} onChange={(v) => set("html", v)} />;

    default:
      /* A type this build does not know about is left alone rather than being
         rewritten into something the backend cannot read. */
      return (
        <div style={{ fontSize: 12.5, color: "#b45309" }}>
          This section type ({type}) is not editable in this version.
        </div>
      );
  }
};

/* ── One section card ─────────────────────────────────────────────────────── */
const SectionCard = ({ section, editing, onEdit, onCancel, onSave, onDelete, saving, company }) => {
  const [draft, setDraft] = useState(section);
  useEffect(() => { setDraft(section); }, [section]);

  const editable = editing;
  const disabled = draft.enabled === false;

  return (
    <div style={{ ...card, opacity: disabled && !editable ? 0.75 : 1 }}>
      <div style={cardHead}>
        <span style={cardTitle}>{draft.title || TYPE_LABEL[draft.type] || draft.type}</span>
        <span style={{ fontSize: 11, background: "#f5f3ff", color: "#5b48e8", border: "1px solid #ddd6fe", borderRadius: 20, padding: "1px 9px", fontWeight: 700 }}>
          {TYPE_LABEL[draft.type] || draft.type}
        </span>
        <span style={{ fontFamily: "monospace", fontSize: 11, color: "#9ca3af" }}>{draft.key}</span>

        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          <Switch
            checked={draft.enabled !== false}
            disabled={!editable}
            onChange={(v) => setDraft({ ...draft, enabled: v })}
          />
          <span style={{ fontSize: 12, fontWeight: 600, color: disabled ? "#9ca3af" : "#16a34a" }}>
            {disabled ? "Hidden" : "Enabled"}
          </span>
          {editable ? (
            <>
              <button
                onClick={() => { setDraft(section); onCancel(); }}
                disabled={saving}
                style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", color: "#6b7280", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}
              >
                Cancel
              </button>
              <button
                onClick={() => onSave(draft)}
                disabled={saving}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "none", background: "#5b48e8", color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}
              >
                <SaveOutlined /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onEdit}
                style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid #ddd6fe", background: "#f5f3ff", color: "#5b48e8", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}
              >
                Edit
              </button>
              <Tooltip title="Delete this section">
                <button
                  onClick={onDelete}
                  style={{ width: 30, height: 30, borderRadius: 7, border: "1px solid #fecaca", background: "#fef2f2", color: "#dc2626", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}
                >
                  <DeleteOutlined />
                </button>
              </Tooltip>
            </>
          )}
        </div>
      </div>

      <div style={body}>
        <TextField
          title="Section heading" value={draft.title} editable={editable}
          onChange={(v) => setDraft({ ...draft, title: v })}
        />
        <SectionContent
          type={draft.type}
          content={draft.content}
          editable={editable}
          company={company}
          onChange={(content) => setDraft({ ...draft, content })}
        />
      </div>
    </div>
  );
};

/* ── Order editor ─────────────────────────────────────────────────────────── */
const OrderEditor = ({ sections, editable, order, onChange }) => {
  const dragIndex = useRef(null);
  const byKey = Object.fromEntries(sections.map((s) => [s.key, s]));

  const move = (from, to) => {
    if (from == null || to == null || from === to || to < 0 || to >= order.length) return;
    const next = [...order];
    const [key] = next.splice(from, 1);
    next.splice(to, 0, key);
    onChange(next);
  };

  const row = {
    display: "flex", alignItems: "center", gap: 12, padding: "11px 14px",
    border: "1px solid #eef2ff", borderRadius: 10, background: "#fff", marginBottom: 8,
  };
  const iconBtn = (off) => ({
    width: 26, height: 26, borderRadius: 6, border: "1px solid #e5e7eb", background: "#fff",
    color: off ? "#d1d5db" : "#6b7280", cursor: off ? "not-allowed" : "pointer",
    display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11,
  });

  return (
    <div>
      {order.map((key, i) => {
        const section = byKey[key];
        return (
          <div
            key={key}
            style={row}
            draggable={editable}
            onDragStart={() => { dragIndex.current = i; }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => { move(dragIndex.current, i); dragIndex.current = null; }}
          >
            {editable && (
              <Tooltip title="Drag to reorder">
                <span style={{ cursor: "grab", color: "#c7cbe0", fontSize: 15 }}><HolderOutlined /></span>
              </Tooltip>
            )}
            <span style={{ width: 20, fontSize: 12, fontWeight: 700, color: "#9ca3af" }}>{i + 1}.</span>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: "#111827", flex: 1 }}>
              {section?.title || TYPE_LABEL[section?.type] || key}
              {section?.enabled === false && (
                <span style={{ marginLeft: 8, fontSize: 11, color: "#b45309", fontWeight: 700 }}>Hidden</span>
              )}
            </span>
            {editable && (
              <div style={{ display: "flex", gap: 5 }}>
                <Tooltip title="Move up">
                  <button style={iconBtn(i === 0)} disabled={i === 0} onClick={() => move(i, i - 1)}>
                    <ArrowUpOutlined />
                  </button>
                </Tooltip>
                <Tooltip title="Move down">
                  <button style={iconBtn(i === order.length - 1)} disabled={i === order.length - 1} onClick={() => move(i, i + 1)}>
                    <ArrowDownOutlined />
                  </button>
                </Tooltip>
              </div>
            )}
          </div>
        );
      })}
      <div style={{ fontSize: 11.5, color: "#9ca3af", lineHeight: 1.6, marginTop: 4 }}>
        The cover, the pricing table, the plan features, the cost breakdown and the signatures are
        fixed — only the sections above move. A hidden section keeps its place but does not print.
      </div>
    </div>
  );
};

/* ── Add section ──────────────────────────────────────────────────────────── */
const AddSectionModal = ({ doc, onClose, onAdd, saving }) => {
  const [type, setType] = useState("richtext");
  const [title, setTitle] = useState("");
  const [error, setError] = useState("");

  const submit = () => {
    if (!title.trim()) return setError("Give the section a heading");
    setError("");
    onAdd({ type, title: title.trim(), content: blankContent(type), enabled: true });
  };

  return (
    <Modal
      open
      title={<span style={{ fontWeight: 700 }}>Add section to the {doc}</span>}
      onCancel={onClose}
      onOk={submit}
      okText={saving ? "Adding…" : "Add section"}
      okButtonProps={{ loading: saving }}
      cancelButtonProps={{ disabled: saving }}
      destroyOnClose
      width={460}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 4 }}>
        <div>
          <label style={label}>Type</label>
          <Select
            value={type} onChange={setType} style={{ width: "100%" }}
            options={ADDABLE_TYPES.map((t) => ({ value: t, label: TYPE_LABEL[t] }))}
          />
          <div style={{ fontSize: 11.5, color: "#9ca3af", marginTop: 6 }}>
            Rich text is the free-form one. The others are lists the document numbers or bullets
            for you.
          </div>
        </div>
        <div>
          <label style={label}>Heading <span style={{ color: "#dc2626" }}>*</span></label>
          <Input
            value={title}
            onChange={(e) => { setTitle(e.target.value); setError(""); }}
            placeholder="e.g. Implementation timeline"
          />
        </div>
        <div style={{ fontSize: 11.5, color: "#9ca3af" }}>
          It is added at the end — move it on the <b>Section order</b> tab.
        </div>
        {error && <div style={{ color: "#dc2626", fontSize: 12.5 }}>{error}</div>}
      </div>
    </Modal>
  );
};

/* ── Page ─────────────────────────────────────────────────────────────────── */
const ProposalContent = () => {
  const { data: template, isLoading, refetch, isFetching } = useProposalTemplate();
  const patchShared = usePatchProposalTemplate();
  const reorder = useReorderSections();
  const updateSection = useUpdateSection();
  const addSection = useAddSection();
  const removeSection = useDeleteSection();
  const mayEdit = isSuperAdmin();

  const [doc, setDoc] = useState("proposal");
  const [tab, setTab] = useState("sections");     // sections | order | shared
  const [editingKey, setEditingKey] = useState(null);
  const [adding, setAdding] = useState(false);
  const [orderDraft, setOrderDraft] = useState(null);
  const [shared, setShared] = useState(null);

  const sections = template?.[doc]?.sections || [];

  /* Deep copy: the shared block is edited locally so Cancel has something to
     fall back to. Sections are edited inside their own card. */
  useEffect(() => {
    if (!template) return;
    setShared(JSON.parse(JSON.stringify({
      cover: template.cover || {},
      footer_note: template.footer_note || "",
      validity_days: template.validity_days ?? 30,
    })));
  }, [template]);

  // Switching document drops any half-finished edit rather than carrying it over.
  useEffect(() => { setEditingKey(null); setOrderDraft(null); }, [doc]);

  if (isLoading || !template || !shared) {
    return (
      <div className="main-content"><div className="page-content"><div className="container-fluid">
        <div style={{ ...card, padding: 24 }}><Skeleton active paragraph={{ rows: 8 }} /></div>
      </div></div></div>
    );
  }

  const order = orderDraft || sections.map((s) => s.key);
  const sharedEditable = mayEdit && editingKey === "__shared__";

  const setSharedAt = (path, value) =>
    setShared((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      let node = next;
      for (let i = 0; i < path.length - 1; i += 1) {
        if (node[path[i]] === undefined) node[path[i]] = {};
        node = node[path[i]];
      }
      node[path[path.length - 1]] = value;
      return next;
    });

  const saveSection = (draft) =>
    updateSection.mutate(
      {
        doc,
        key: draft.key,
        // content is replaced whole — the API does not merge it.
        patch: { title: draft.title, enabled: draft.enabled !== false, content: draft.content },
      },
      { onSuccess: () => setEditingKey(null) },
    );

  const confirmDelete = (section) =>
    Swal.fire({
      title: `Delete "${section.title || section.key}"?`,
      text: "It will be removed from this document.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete",
      reverseButtons: true,
    }).then((r) => {
      if (r.isConfirmed) removeSection.mutate({ doc, key: section.key });
    });

  const tabBtn = (key, text) => {
    const on = tab === key;
    return (
      <button
        key={key}
        onClick={() => { setTab(key); setEditingKey(null); setOrderDraft(null); }}
        style={{
          padding: "8px 18px", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer",
          border: `1.5px solid ${on ? "#5b48e8" : "#e5e7eb"}`,
          background: on ? "#5b48e8" : "#fff", color: on ? "#fff" : "#6b7280",
        }}
      >
        {text}
      </button>
    );
  };

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">

          {/* Header */}
          <div style={{ background: "linear-gradient(135deg,#5b48e8 0%,#7c3aed 100%)", borderRadius: 16, padding: "26px 30px", marginBottom: 20, boxShadow: "0 8px 32px rgba(91,72,232,0.28)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ width: 54, height: 54, background: "rgba(255,255,255,.22)", borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, color: "white" }}>
                <FileTextOutlined />
              </div>
              <div>
                <h4 style={{ color: "white", margin: 0, fontSize: 23, fontWeight: 700 }}>Proposal &amp; Quotation Content</h4>
                <p style={{ color: "rgba(255,255,255,.85)", margin: 0, fontSize: 14 }}>
                  Every word the documents print
                </p>
              </div>
            </div>
            <Tooltip title="Refresh">
              <button
                onClick={() => refetch()}
                style={{ width: 40, height: 40, borderRadius: 10, border: "1px solid rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.1)", color: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}
              >
                <ReloadOutlined spin={isFetching} />
              </button>
            </Tooltip>
          </div>

          {!mayEdit && (
            <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 12, padding: "10px 16px", marginBottom: 18, fontSize: 13, color: "#92400e" }}>
              You can read this content, but only a superadmin can edit it.
            </div>
          )}

          {/* Document toggle — two documents, two section lists. */}
          <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
            {DOCS.map((d) => {
              const on = doc === d.key;
              return (
                <button
                  key={d.key}
                  onClick={() => setDoc(d.key)}
                  style={{
                    padding: "9px 24px", borderRadius: 10, fontSize: 13.5, fontWeight: 700, cursor: "pointer",
                    border: `1.5px solid ${on ? "#0d9488" : "#e5e7eb"}`,
                    background: on ? "#0d9488" : "#fff", color: on ? "#fff" : "#6b7280",
                  }}
                >
                  {d.label}
                </button>
              );
            })}
            <span style={{ fontSize: 12, color: "#9ca3af", marginLeft: 4 }}>
              {sections.length} section{sections.length === 1 ? "" : "s"}
            </span>
          </div>

          {/* Tabs */}
          <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
            {tabBtn("sections", "Sections")}
            {tabBtn("order", "Section order")}
            {tabBtn("shared", "Cover & footer")}
          </div>

          {/* ── Sections ── */}
          {tab === "sections" && (
            <>
              {mayEdit && (
                <div style={{ marginBottom: 16 }}>
                  <button
                    onClick={() => setAdding(true)}
                    style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 18px", borderRadius: 10, border: "1px dashed #c7d2fe", background: "#f5f3ff", color: "#5b48e8", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
                  >
                    <PlusOutlined /> Add section
                  </button>
                </div>
              )}
              {sections.length === 0 ? (
                <div style={{ ...card, padding: "40px 20px", textAlign: "center", color: "#9ca3af", fontSize: 13 }}>
                  This document has no sections yet.
                </div>
              ) : (
                sections.map((section) => (
                  <SectionCard
                    key={section.key}
                    section={section}
                    company={template?.company}
                    editing={mayEdit && editingKey === section.key}
                    saving={updateSection.isPending}
                    onEdit={() => setEditingKey(section.key)}
                    onCancel={() => setEditingKey(null)}
                    onSave={saveSection}
                    onDelete={() => confirmDelete(section)}
                  />
                ))
              )}
            </>
          )}

          {/* ── Section order ── */}
          {tab === "order" && (
            <div style={card}>
              <div style={cardHead}>
                <span style={cardTitle}>Section order</span>
                <span style={{ fontSize: 12, color: "#9ca3af" }}>Drag, or use the arrows</span>
                {mayEdit && (
                  <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                    {orderDraft ? (
                      <>
                        <button
                          onClick={() => setOrderDraft(null)}
                          disabled={reorder.isPending}
                          style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", color: "#6b7280", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => reorder.mutate({ doc, order: orderDraft }, { onSuccess: () => setOrderDraft(null) })}
                          disabled={reorder.isPending}
                          style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "none", background: "#5b48e8", color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}
                        >
                          <SaveOutlined /> {reorder.isPending ? "Saving…" : "Save order"}
                        </button>
                      </>
                    ) : (
                      <span style={{ fontSize: 12, color: "#9ca3af" }}>Move a section to start editing</span>
                    )}
                  </div>
                )}
              </div>
              <div style={body}>
                <OrderEditor
                  sections={sections} order={order} editable={mayEdit}
                  onChange={setOrderDraft}
                />
              </div>
            </div>
          )}

          {/* ── Shared: company, cover subtitle, footer ── */}
          {tab === "shared" && (
            <>
              {/* Company details are edited on the Company Profile screen — the
                  documents read them live, so a second copy here would only
                  invite the two to disagree. */}
              <div style={{ background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 12, padding: "11px 16px", marginBottom: 20, fontSize: 12.5, color: "#0f766e", lineHeight: 1.6 }}>
                Company name, GSTIN, CIN, SAC, signatory and bank details come from{" "}
                <b>Company Profile</b>. Both documents pick them up automatically.
              </div>
              <div style={card}>
                <div style={cardHead}>
                  <span style={cardTitle}>Cover subtitle</span>
                  <span style={{ fontSize: 12, color: "#9ca3af" }}>The line under the cover headline</span>
                  {/* One Edit/Save for everything left on this tab — the cover
                      subtitle, the footer note and the validity all go in one
                      patch. */}
                  {mayEdit && (
                    <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                      {sharedEditable ? (
                        <>
                          <button
                            onClick={() => { setShared(JSON.parse(JSON.stringify({ cover: template.cover || {}, footer_note: template.footer_note || "", validity_days: template.validity_days ?? 30 }))); setEditingKey(null); }}
                            disabled={patchShared.isPending}
                            style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", color: "#6b7280", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => patchShared.mutate(shared, { onSuccess: () => setEditingKey(null) })}
                            disabled={patchShared.isPending}
                            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "none", background: "#5b48e8", color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}
                          >
                            <SaveOutlined /> {patchShared.isPending ? "Saving…" : "Save"}
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => setEditingKey("__shared__")}
                          style={{ padding: "6px 16px", borderRadius: 8, border: "1px solid #ddd6fe", background: "#f5f3ff", color: "#5b48e8", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div style={body}>
                  <HtmlField title="Corporate" rows={3} editable={sharedEditable}
                    value={shared.cover?.subtitle?.corporate}
                    onChange={(v) => setSharedAt(["cover", "subtitle", "corporate"], v)} />
                  <HtmlField title="Attorney" rows={3} editable={sharedEditable}
                    value={shared.cover?.subtitle?.attorney}
                    onChange={(v) => setSharedAt(["cover", "subtitle", "attorney"], v)} />
                </div>
              </div>

              <div style={card}>
                <div style={cardHead}><span style={cardTitle}>Footer</span></div>
                <div style={body}>
                  <HtmlField title="Footer note" rows={3} editable={sharedEditable}
                    value={shared.footer_note} onChange={(v) => setSharedAt(["footer_note"], v)} />
                  <div style={cols()}>
                    <NumField title="Validity" suffix="days" value={shared.validity_days}
                      editable={sharedEditable} onChange={(v) => setSharedAt(["validity_days"], v)} />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {adding && (
        <AddSectionModal
          doc={doc}
          saving={addSection.isPending}
          onClose={() => setAdding(false)}
          onAdd={(section) =>
            addSection.mutate({ doc, section }, { onSuccess: () => setAdding(false) })}
        />
      )}
    </div>
  );
};

export default ProposalContent;
